import {addTime, days, endOfDay, isSameDayUtc, startOfDay, startOfDayUTC, toDate} from '@/utils/dates';
import {Logger} from '@vanti/vue-logger';
import {byRef} from '@/utils/firestore';

const log = Logger.get('bookings');

/**
 * Return the key for the reservation that reserves the given resource path.
 *
 * @param {Booking} booking
 * @param {string} resourcePath
 * @return {string|null}
 */
export function reservationKey(booking, resourcePath) {
  if (!booking) return null;
  for (const [key, reservation] of Object.entries(booking.reservations)) {
    if (reservation.resource && reservation.resource.ref.path === resourcePath) {
      return key;
    }
  }
  return null;
}

const CHECK_IN_PROP = 'checkInPeriod';
export const BOOKED_FROM_PROP = 'bookedPeriod.fromTime';
export const BOOKED_TO_PROP = 'bookedPeriod.toTime';

/**
 * @param {string} reservationKey
 * @return {string[]}
 */
export function scheduleProps(reservationKey) {
  return [
    BOOKED_FROM_PROP, BOOKED_TO_PROP,
    reservedFromProp(reservationKey), reservedToProp(reservationKey),
    checkInFromProp(reservationKey), checkInToProp(reservationKey)
  ];
}

/**
 * @param {string} reservationKey
 * @return {string}
 */
export function checkInProp(reservationKey) {
  return `reservations.${reservationKey}.${CHECK_IN_PROP}`;
}

/**
 * @param {string} reservationKey
 * @return {string}
 */
export function checkInFromProp(reservationKey) {
  return checkInProp(reservationKey) + '.fromTime';
}

/**
 * @param {string} reservationKey
 * @return {string}
 */
export function checkInToProp(reservationKey) {
  return checkInProp(reservationKey) + '.toTime';
}

/**
 * @param {string} reservationKey
 * @return {string}
 */
function reservedProp(reservationKey) {
  return `reservations.${reservationKey}.reservedPeriod`;
}

/**
 * @param {string} reservationKey
 * @return {string}
 */
export function reservedFromProp(reservationKey) {
  return reservedProp(reservationKey) + '.fromTime';
}

/**
 * @param {string} reservationKey
 * @return {string}
 */
export function reservedToProp(reservationKey) {
  return reservedProp(reservationKey) + '.toTime';
}

/**
 * Get the from time from the booking
 *
 * @param {Booking} b
 * @param {string} reservationKey
 * @return {CanBeDate}
 */
export function fromTime(b, reservationKey) {
  if (!b) return null;
  const reservation = b.reservations && b.reservations[reservationKey];
  if (reservation && reservation.reservedPeriod && reservation.reservedPeriod.fromTime) {
    return reservation.reservedPeriod.fromTime;
  }
  return b.bookedPeriod && b.bookedPeriod.fromTime;
}

/**
 * Get the to time from the booking
 *
 * @param {Booking} b
 * @param {string} reservationKey
 * @return {CanBeDate}
 */
export function toTime(b, reservationKey) {
  if (!b) return null;
  const reservation = b.reservations && b.reservations[reservationKey];
  if (reservation && reservation.reservedPeriod && reservation.reservedPeriod.toTime) {
    return reservation.reservedPeriod.toTime;
  }
  return b.bookedPeriod && b.bookedPeriod.toTime;
}

/**
 * Returns true if the booking is approved.
 *
 * @param {Booking} booking
 * @param {string} reservationKey
 * @return {Object}
 */
export function approval(booking, reservationKey) {
  const reservation = booking && booking.reservations && booking.reservations[reservationKey];
  return reservation && reservation.approval;
}

/**
 * @param {Booking} b
 * @param {string} reservationKey
 * @return {{fromTime: CanBeDate, toTime: CanBeDate}|undefined}
 */
export function checkInPeriod(b, reservationKey) {
  return b.reservations[reservationKey][CHECK_IN_PROP];
}

/**
 * @param {Booking} b
 * @param {string} reservationKey
 * @return {boolean}
 */
export function hasCheckIn(b, reservationKey) {
  return b.reservations[reservationKey].hasOwnProperty(CHECK_IN_PROP);
}

/**
 * @param {Booking} booking
 * @param {string} reservationKey
 * @return {boolean}
 */
export function requiresCheckIn(booking, reservationKey) {
  return Boolean(booking.reservations[reservationKey].requiresCheckIn);
}

/**
 * Sets up a snapshot listener for bookings that reserve the given resource.
 *
 * @param {firebase.firestore.DocumentReference} resourceRef
 * @param {{startTime: CanBeDate, endTime: CanBeDate}} queryPeriod
 * @param {function(firebase.firestore.QuerySnapshot)} cb
 * @return {Object<string, function()>}
 */
export function watchBookingsForResource(resourceRef, queryPeriod, cb) {
  const bookingsRef = resourceRef.parent.parent.collection('bookings');

  const query = bookingsRef
      .where('_reservations_resource_ref', 'array-contains', resourceRef)
      .where('_boundsPeriod.fromTime', '>=', toDate(queryPeriod.startTime))
      .where('_boundsPeriod.fromTime', '<', toDate(queryPeriod.endTime));
  return {
    [resourceRef.path]: query.onSnapshot(
        snap => cb(snap),
        err => log.error(`watchBookingsForResource ${resourceRef.path}`, err)
    )
  };
}

/**
 * Sets up a snapshot listener for bookings that reserve the given resource.
 *
 * @param {firebase.firestore.DocumentReference} resourceRef
 * @param {CanBeDate} date
 * @param {function(firebase.firestore.QuerySnapshot)} cb
 * @return {Object<string, function()>}
 */
export function watchBookingsForResourceOnDate(resourceRef, date, cb) {
  const bookingsRef = resourceRef.parent.parent.collection('bookings');

  const d = toDate(date);
  const start = startOfDay(d);
  const end = endOfDay(d);
  const startDate = start.toISOString().substring(0, 10);
  const endDate = end.toISOString().substring(0, 10);

  const cancellers = [];

  const query = bookingsRef
      .where('_reservations_resource_ref', 'array-contains', resourceRef)
      .where(`_boundsPeriodSpans.${startDate}`, '==', true);
  const cancelSnap = query.onSnapshot(
      snap => cb(snap),
      err => log.error(`watchBookingsForResource ${resourceRef.path}`, err)
  );
  cancellers.push(cancelSnap);
  if (startDate !== endDate) { // e.g. in time zones which aren't UTC
    const query = bookingsRef
        .where('_reservations_resource_ref', 'array-contains', resourceRef)
        .where(`_boundsPeriodSpans.${endDate}`, '==', true);
    const cancelSnap = query.onSnapshot(
        snap => cb(snap),
        err => log.error(`watchBookingsForResource ${resourceRef.path}`, err)
    );
    cancellers.push(cancelSnap);
  }
  return {
    [resourceRef.path]: () => cancellers.forEach(canceller => canceller())
  };
}

/**
 * Create a new ad hoc booking document.
 *
 * @param {Date|firebase.firestore.Timestamp} fromTime
 * @param {Date|firebase.firestore.Timestamp} toTime
 * @param {Object} resource
 * @param {kahu.firestore.Creatable} [creatableSnippet]
 * @return {Booking}
 */
export function newAdHocBooking(fromTime, toTime, resource, creatableSnippet) {
  const creatable = creatableSnippet || {};
  const booking = /** @type {Booking} */ {
    ...creatable,
    title: 'Ad Hoc Meeting',
    bookedPeriod: {fromTime, toTime},
    reservations: {
      r1: {
        resource,
        requiresCheckIn: false
      }
    }
  };
  // pre-populate computed properties, so this booking gets picked up in the query immediately
  booking._boundsPeriod = {fromTime, toTime};
  if (resource.ref) {
    booking['_reservations_resource_ref'] = [resource.ref];
    if (resource.kind && resource.kind.ref) {
      booking['_reservations_resource_kind_ref'] = [resource.kind.ref];
    }
  }
  const spans = calcBoundsPeriodSpans(fromTime, toTime);
  if (spans) {
    booking['_boundsPeriodSpans'] = spans;
  }
  return booking;
}

/**
 * @param {Date|firebase.firestore.Timestamp} fromTime
 * @param {Date|firebase.firestore.Timestamp} toTime
 * @return {null|Object<string, Boolean>}
 */
export function calcBoundsPeriodSpans(fromTime, toTime) {
  if (!fromTime || !toTime) return null;

  const boundsDays = [];
  const from = typeof fromTime.toDate === 'function' ? fromTime.toDate() : fromTime;
  const to = typeof toTime.toDate === 'function' ? toTime.toDate() : toTime;
  let day = startOfDayUTC(from);
  let count = 0;
  const MAX_DAYS = 365;

  boundsDays.push(day);
  while (!isSameDayUtc(day, to)) { // note: check UTC day here, not local
    day = addTime(day, days);
    boundsDays.push(day);
    count++;

    if (count > MAX_DAYS) {
      break;
    }
  }

  return boundsDays.reduce((spans, day) => {
    const iso = day.toISOString();
    spans[iso.substring(0, 10)] = true;
    spans[iso.substring(0, 7)] = true; // month
    spans[iso.substring(0, 4)] = true; // year
    return spans;
  }, {});
}

/**
 * @param {Booking} booking
 * @return {number}
 */
export function connectionCount(booking) {
  return booking._connectionCount || 0;
}

/**
 * @param {Booking} booking
 * @return {string}
 */
export function connectionRefKey(booking) {
  return byRef(booking.connection && booking.connection.ref);
}

/**
 * @param {Booking} booking
 * @param {firebase.firestore.DocumentReference} resourceRef
 * @return {boolean}
 */
export function bookingReservesResource(booking, resourceRef) {
  for (const reservation of Object.values(booking.reservations)) {
    if (reservation.resource && reservation.resource.ref && reservation.resource.ref.isEqual(resourceRef)) {
      return true;
    }
  }
  return false;
}

/**
 * @param {Booking} booking
 * @param {string} reservationKey
 * @return {DocumentSnippet|null}
 */
export function bookingReservedResource(booking, reservationKey) {
  const reservation = booking && booking.reservations[reservationKey];
  return reservation && reservation.resource;
}
